import { intersection } from 'underscore';

import { store } from '@/store/main';
import { PermissionObject, parsePermission } from './utils';
import { getLogger } from '@/utils/debug-logger';
import { BLAdminWithPermissions } from '@/connection/access/get-channel-access';
import { allAccessRoles, rolePermissionSortOrder } from './constants';
import { EPermissions, EPermissionsDisplayNames } from '@/types/permissions.types';
import { CheckboxTreeData } from '@ui/checkbox-tree/v2-checkbox-tree';

const log = getLogger('bl-admin:access');

export class Access implements PermissionObject {
  constructor(
    public groupUuid?: string,
    public eventUuid?: string,
    public sessionUuid?: string,
    public allGroupsInChannel?: boolean,
    public allEventsInGroup?: boolean,
    public allSessionsInEvent?: boolean
  ) {}

  public static fromPermissionStatement(statement: string): Access {
    const permissions = parsePermission(statement);
    return Access.fromPermissionObject(permissions);
  }

  public static fromPermissionObject(permissions: PermissionObject): Access {
    return new Access(
      permissions.groupUuid,
      permissions.eventUuid,
      permissions.sessionUuid,
      permissions.allGroupsInChannel,
      permissions.allEventsInGroup,
      permissions.allSessionsInEvent
    );
  }

  get allAccess() {
    return !!this.allGroupsInChannel;
  }

  public canAccessProject(eventUuid: string): boolean {
    if (this.allAccess) {
      return true;
    }

    if (this.groupUuid && this.allEventsInGroup) {
      const projectGroups = store.getState().projects.projectGroups || [];
      const group = projectGroups.find((group) => group.uuid === this.groupUuid);
      if (group) {
        return group.event_uuids.includes(eventUuid);
      }
    }

    return this.eventUuid === eventUuid;
  }

  public canAccessGroup(groupUuid: string): boolean {
    if (this.allGroupsInChannel) {
      return true;
    }

    return this.groupUuid === groupUuid;
  }

  public canAccessSession(sessionUuid: string): boolean {
    return this.sessionUuid === sessionUuid;
  }

  public toResourceRequest() {
    return {
      groupUuid: this.groupUuid,
      eventUuid: this.eventUuid,
      sessionUuid: this.sessionUuid,
    };
  }

  public toJSON() {
    return {
      groupUuid: this.groupUuid,
      eventUuid: this.eventUuid,
      sessionUuid: this.sessionUuid,
      allGroupsInChannel: this.allGroupsInChannel,
      allEventsInGroup: this.allEventsInGroup,
      allSessionsInEvent: this.allSessionsInEvent,
    };
  }

  public static fromJSON(json: ReturnType<Access['toJSON']>): Access {
    return new Access(
      json.groupUuid,
      json.eventUuid,
      json.sessionUuid,
      json.allGroupsInChannel,
      json.allEventsInGroup,
      json.allSessionsInEvent
    );
  }

  public toString() {
    const parts = [];

    let groupSet = false;
    if (this.allGroupsInChannel !== undefined || this.groupUuid !== undefined) {
      if (this.allGroupsInChannel) {
        return 'groups.*';
      } else if (this.groupUuid) {
        parts.push(`groups.${this.groupUuid}`);
      }

      groupSet = true;
    }

    let eventSet = false;
    if (this.allEventsInGroup !== undefined || this.eventUuid !== undefined) {
      if (!groupSet) {
        parts.push('*');
      }

      if (this.allEventsInGroup) {
        parts.push('events.*');
      } else if (this.eventUuid) {
        parts.push(`events.${this.eventUuid}`);
      }

      eventSet = true;
    }

    if (this.allSessionsInEvent !== undefined || this.sessionUuid !== undefined) {
      if (!eventSet) {
        parts.push('*');
      }

      if (this.allSessionsInEvent) {
        parts.push('sessions.*');
      } else if (this.sessionUuid) {
        parts.push(`sessions.${this.sessionUuid}`);
      }
    }

    const statement = parts.join('.').replace(/-/g, '');

    log(`Generated permission statement: ${statement}`);
    return statement;
  }
}

type CheckOptions = {
  inNext?: boolean;
};

export class UserAccess {
  private nextPermissions: Access[] = [];
  private nextRoles: Set<EPermissions>;
  public roles: EPermissions[] = [];

  constructor(
    public userId: string,
    public permissions: Access[],
    public user: BLAdminWithPermissions,
    nextPermissions?: Access[],
    nextRoles?: Set<EPermissions>
  ) {
    this.nextPermissions = [...(nextPermissions ?? permissions)];
    this.nextRoles = nextRoles ?? new Set(user.roles);
    this.roles = user.roles;
  }

  public static from(access: UserAccess) {
    return new UserAccess(access.userId, access.permissions, access.user, access.nextPermissions, access.nextRoles);
  }

  public toJSON() {
    return {
      userId: this.userId,
      permissions: this.permissions.map((p) => p.toJSON()),
      user: this.user,
      nextPermissions: this.nextPermissions.map((p) => p.toJSON()),
      nextRoles: Array.from(this.nextRoles),
      roles: this.roles,
    };
  }

  public static fromJSON(json: {
    userId: string;
    permissions: ReturnType<Access['toJSON']>[];
    user: BLAdminWithPermissions;
    nextPermissions: ReturnType<Access['toJSON']>[];
    nextRoles: EPermissions[];
    roles: EPermissions[];
  }): UserAccess {
    const instance = new UserAccess(
      json.userId,
      json.permissions.map((p) => Access.fromJSON(p)),
      json.user,
      json.nextPermissions.map((p) => Access.fromJSON(p)),
      new Set(json.nextRoles)
    );
    instance.roles = json.roles;
    return instance;
  }

  private groupFromProject(eventUuid: string): string | undefined {
    const projectGroups = store.getState().projects.projectGroups || [];
    const projectInGroup = projectGroups.find((group) => {
      return group.event_uuids.includes(eventUuid);
    });
    if (projectInGroup) {
      return projectInGroup.uuid;
    }
  }

  private projectsInGroup(groupUuid: string): string[] | undefined {
    const projectGroups = store.getState().projects.projectGroups || [];
    const group = projectGroups.find((group) => group.uuid === groupUuid);
    return group?.event_uuids;
  }

  // should return true if direct access to this uuid or user has access to all groups
  // or if the event is in a group that the user has access to
  private getProjectGroup(groupUuid: string) {
    const projectGroups = store.getState().projects.projectGroups || [];
    return projectGroups.find((group) => group.uuid === groupUuid);
  }

  private getUngroupedProjects() {
    const projectGroups = store.getState().projects.projectGroups || [];
    const groupedProjects = projectGroups.reduce<string[]>((acc, group) => {
      acc.push(...group.event_uuids);
      return acc;
    }, []);
    const allProjects = store.getState().projects.projectsList;
    return allProjects.filter((project) => !groupedProjects.includes(project.uuid));
  }

  // should return true if direct access to this uuid or all groups
  public canAccessGroup(groupUuid: string, opts?: CheckOptions): boolean {
    const permissions = opts?.inNext ? this.nextPermissions : this.permissions;
    const canAccess = permissions.some((permission) => permission.allAccess || permission.canAccessGroup(groupUuid));
    // log(`User ${this.userId} can access group ${groupUuid}: ${canAccess}`);
    return canAccess;
  }

  public canAccessProject(eventUuid: string, opts?: CheckOptions): boolean {
    const permissions = opts?.inNext ? this.nextPermissions : this.permissions;
    const groupUuid = this.groupFromProject(eventUuid);
    if (groupUuid && this.canAccessGroup(groupUuid, opts)) {
      // log(`User ${this.userId} can access event ${eventUuid} via group ${groupUuid}`);
      return true;
    }

    const canAccess = permissions.some((permission) => permission.allAccess || permission.canAccessProject(eventUuid));
    // log(`User ${this.userId} can access event ${eventUuid}: ${canAccess}`);
    return canAccess;
  }

  // TODO: create sync method of finding session uuid in events or groups user might be able to access
  // this data does not currently exist anywhere in redux
  public canAccessSession(sessionUuid: string, opts?: CheckOptions): boolean {
    const permissions = opts?.inNext ? this.nextPermissions : this.permissions;
    const canAccess = permissions.some(
      (permission) => permission.allAccess || permission.canAccessSession(sessionUuid)
    );
    log(`User ${this.userId} can access session ${sessionUuid}: ${canAccess}`);
    return canAccess;
  }

  public canAccessAllGroups(): boolean {
    return this.nextPermissions.some((permission) => permission.allAccess);
  }

  // if all-access has been added for this user then remove all other permissions
  // as they will be redundant
  public addAll() {
    this.nextPermissions = [Access.fromPermissionObject({ allGroupsInChannel: true })];
    log(`Adding global access to user ${this.userId}`);
    return this;
  }

  public removeAll() {
    this.nextPermissions = [];
    log(`Removing all access for user ${this.userId}`);
    return this;
  }

  // if all-access is already enabled this does nothing
  public addGroup(groupUuid: string) {
    const groupPermission = Access.fromPermissionObject({ groupUuid, allEventsInGroup: true });

    // user already has all access, do nothing
    if (this.nextPermissions.some((permission) => permission.allAccess)) {
      log(`User ${this.userId} already has all access, not adding group ${groupUuid}`);
      return this;
    } else {
      log(`Adding group ${groupUuid} to user ${this.userId}`);
      const projectsInGroup = this.projectsInGroup(groupUuid);
      if (projectsInGroup) {
        for (const projectUuid of projectsInGroup) {
          this.removeProject(projectUuid);
        }
      }
      this.nextPermissions = [...this.nextPermissions, groupPermission];
    }

    return this;
  }

  // adding a project - only needed if user can't already access it.
  public addProject(projectUuid: string) {
    if (!this.canAccessProject(projectUuid, { inNext: true })) {
      log(`Adding project ${projectUuid} to user ${this.userId}`);
      this.nextPermissions = [
        ...this.nextPermissions,
        Access.fromPermissionObject({ eventUuid: projectUuid, allSessionsInEvent: true }),
      ];
    }

    return this;
  }

  public removeGroup(groupUuid: string) {
    // we are removing a group but user has all access
    // need to revoke all access and add all group uuids except the one we are removing
    if (this.nextPermissions.some((permission) => permission.allAccess)) {
      log(`User ${this.userId} has all access, removing group ${groupUuid} and re-adding all other groups`);
      // add all groups except the removed one
      const projectGroups = store.getState().projects.projectGroups || [];
      const groupPermissions = projectGroups.reduce((acc: Access[], group) => {
        if (group.uuid !== groupUuid) {
          acc.push(Access.fromPermissionObject({ groupUuid: group.uuid, allEventsInGroup: true }));
        }

        return acc;
      }, []);

      // add access for all ungrouped projects individually
      const ungroupedProjects = this.getUngroupedProjects();
      const projectPermissions = ungroupedProjects.reduce<Access[]>((permissions, project) => {
        permissions.push(Access.fromPermissionObject({ eventUuid: project.uuid, allSessionsInEvent: true }));
        return permissions;
      }, []);

      this.nextPermissions = [...groupPermissions, ...projectPermissions];
    } else {
      log(`Removing group ${groupUuid} from user ${this.userId}`);
      const projectsInGroup = this.projectsInGroup(groupUuid);
      log(`Projects in group ${groupUuid}:`, projectsInGroup);

      this.nextPermissions = this.nextPermissions.filter((permission) => {
        // remove all projects in the group the user might access because user access to the group removed
        if (permission.eventUuid && projectsInGroup?.includes(permission.eventUuid)) {
          return false;
        }

        return permission.groupUuid !== groupUuid;
      });

      log(`Remaining permissions for user ${this.userId}:`, this.nextPermissions);
    }

    return this;
  }

  public removeProject(projectUuid: string) {
    // first remove all direct access to this projectUuid
    this.nextPermissions = this.nextPermissions.filter((permission) => permission.eventUuid !== projectUuid);

    // user has all access
    if (this.nextPermissions.some((permission) => permission.allAccess)) {
      log(
        `User ${this.userId} has all access, removing project ${projectUuid} and re-adding all other groups and ungrouped projects`
      );

      // rebuild tree with everything except the projectUuid we are removing
      // and the groups that contain it
      const projectGroups = store.getState().projects.projectGroups || [];
      const groupPermissions = projectGroups.reduce((acc: Access[], group) => {
        // if this group includes this projectUuid, all projects in this group individually
        if (group.event_uuids.includes(projectUuid)) {
          for (const uuid of group.event_uuids) {
            if (uuid !== projectUuid) {
              acc.push(Access.fromPermissionObject({ eventUuid: uuid, allSessionsInEvent: true }));
            }
          }
        } else {
          // otherwise just add the group
          acc.push(Access.fromPermissionObject({ groupUuid: group.uuid, allEventsInGroup: true }));
        }

        return acc;
      }, []);

      // add all ungrouped events except this one individually
      const ungroupedProjects = this.getUngroupedProjects();
      const projectPermissions = ungroupedProjects.reduce<Access[]>((permissions, project) => {
        if (project.uuid !== projectUuid) {
          permissions.push(Access.fromPermissionObject({ eventUuid: project.uuid, allSessionsInEvent: true }));
        }

        return permissions;
      }, []);

      this.nextPermissions = [...groupPermissions, ...projectPermissions];
      return this;
    }

    // if removing this project and user can access it via group access, need to revoke
    // all access to this group and re-add all projects within it except the target project
    const groupUuid = this.groupFromProject(projectUuid);

    log(`User ${this.userId} has an project ${projectUuid} that is in a group ${groupUuid}`);

    // debugger;

    if (
      groupUuid &&
      this.nextPermissions.some((permission) => permission.groupUuid === groupUuid && permission.allEventsInGroup)
    ) {
      log(
        `User ${this.userId} has all projects in group access, removing project ${projectUuid} and re-adding all other projects in group ${groupUuid}`
      );
      const group = this.getProjectGroup(groupUuid);

      // remove the group
      this.nextPermissions = this.nextPermissions.filter((permission) => permission.groupUuid !== groupUuid);

      // re-add all projects in group except the one we are removing
      const projectPermissions =
        group?.event_uuids.reduce<Access[]>((permissions, uuid) => {
          if (uuid !== projectUuid) {
            permissions.push(Access.fromPermissionObject({ eventUuid: uuid, allSessionsInEvent: true }));
          }

          return permissions;
        }, []) || [];

      this.nextPermissions.push(...projectPermissions);
    } else {
      log(`Removing project ${projectUuid} from user ${this.userId}`);
      this.nextPermissions = this.nextPermissions.filter((permission) => permission.eventUuid !== projectUuid);
    }

    log(`Remaining permissions for user ${this.userId}:`, this.nextPermissions);

    return this;
  }

  public revert() {
    log(`Reverting permission changes for user ${this.userId}`);
    this.nextPermissions = [...this.permissions];
    this.nextRoles = new Set(this.user.roles);
    return this;
  }

  public commit() {
    log(`Committing permission changes for user ${this.userId}`);
    this.permissions = [...this.nextPermissions];
    this.roles = [...this.nextRoles];
    this.nextPermissions = this.permissions;
    this.nextRoles = new Set(this.roles);
    return this;
  }

  public getNextPermissions() {
    return this.nextPermissions;
  }

  public willChangeRoles() {
    return JSON.stringify(this.roles) !== JSON.stringify(Array.from(this.nextRoles));
  }

  public willChangePermissions() {
    return JSON.stringify(this.permissions) !== JSON.stringify(this.nextPermissions);
  }

  public getNextRoles() {
    return Array.from(this.nextRoles);
  }

  public willLoseAdminPrivileges() {
    return this.roles.includes(EPermissions.Admin) && !this.nextRoles.has(EPermissions.Admin);
  }

  public addRole(role: EPermissions) {
    if (role === EPermissions.Owner || role === EPermissions.Admin) {
      this.nextRoles.clear();
    }

    if (this.nextRoles.has(EPermissions.Admin) || this.nextRoles.has(EPermissions.Owner)) {
      this.nextRoles.clear();
    }

    this.nextRoles.add(role);

    if (allAccessRoles.includes(role)) {
      this.addAll();
    }

    log('Added role', role, 'to user', this.userId);
    return this;
  }

  public removeRole(role: EPermissions) {
    if (this.nextRoles.size === 1) {
      log(`User ${this.userId} only has one role and at least one role is required.`);
      return this;
    }

    this.nextRoles.delete(role);
    log('Removed role', role, 'from user', this.userId);
    return this;
  }

  public canChangeRoleTo(fromUser: EPermissions[], role: EPermissions) {
    const canChange = [EPermissions.Owner, EPermissions.Admin, EPermissions.Builder, EPermissions.GroupAdmin];
    if (!intersection(canChange, fromUser).length) {
      return false;
    }

    if (fromUser.includes(EPermissions.Owner)) {
      return true;
    }

    if (fromUser.includes(EPermissions.Admin)) {
      return role !== EPermissions.Owner;
    }

    if (fromUser.includes(EPermissions.Builder)) {
      return role !== EPermissions.Owner && role !== EPermissions.Admin;
    }

    if (fromUser.includes(EPermissions.GroupAdmin)) {
      return [EPermissions.Editor, EPermissions.Moderation, EPermissions.Analytics, EPermissions.Producer].includes(
        role
      );
    }

    return false;
  }

  public rolesList = (fromUser: EPermissions[]): CheckboxTreeData => {
    return {
      id: 'all',
      label: 'All',
      isOn: false,
      children: Object.entries(EPermissions)
        .filter(filterSelectableRoles)
        .map(([key, role]) => {
          return {
            id: key,
            label: EPermissionsDisplayNames[role] ?? role,
            disabled: !this.canChangeRoleTo(fromUser, role),
            metadata: {
              role,
            },
            isOn: this.nextRoles.has(role),
          };
        })
        .sort((a, b) => {
          if (
            rolePermissionSortOrder[a.metadata.role as EPermissions] <
            rolePermissionSortOrder[b.metadata.role as EPermissions]
          ) {
            return -1;
          } else {
            return 1;
          }
        }),
    };
  };
}

function filterSelectableRoles([, role]: [string, EPermissions]) {
  return !!rolePermissionSortOrder[role];
}
